/**
 * useWindowPresetHotkeys - Keyboard shortcuts for window presets
 *
 * Shortcuts:
 * - Shift+1..5 / Shift+T/L/B/R/V — plain-key bindings, gated on the rad's
 *   mouse hovering anything except the report editor (so capital letters
 *   `!@#$%TLBRV` typed into the report don't fire viewer presets).
 * - Alt+1..5 / Alt+T/L/B/R/V — modifier bindings, fire from anywhere
 *   (including from inside the report editor while dictating). Gives the
 *   rad a way to hit a preset without moving the mouse off the editor.
 */

import { useHotkeys } from "react-hotkeys-hook";
import { useAtomValue } from "jotai";
import { WINDOW_PRESETS, WindowPreset } from "../constants/windowPresets";
import { hoveredSurfaceAtom } from "../atoms/hoveredSurfaceAtom";

interface UseWindowPresetHotkeysOptions {
  onPresetSelect: (preset: WindowPreset, center: number, width: number) => void;
  enabled?: boolean;
}

const NUMBER_KEYS = [
  ...WINDOW_PRESETS.map((p) => p.numberKey.toLowerCase()),
  ...WINDOW_PRESETS.map((p) => p.numberKey.replace(/^Shift\+/, "")),
].join(",");

const LETTER_KEYS = WINDOW_PRESETS.map((p) => p.letterKey.toLowerCase()).join(",");

export function useWindowPresetHotkeys({
  onPresetSelect,
  enabled = true,
}: UseWindowPresetHotkeysOptions) {
  const hoveredSurface = useAtomValue(hoveredSurfaceAtom);
  const plainKeyEnabled = enabled && hoveredSurface !== "editor";

  // Shift+number / bare number — hover-gated. The CE flag lets the
  // binding fire even when keyboard focus is in the report editor: the
  // mouse-hover gate already silences these while the rad's mouse is
  // over the editor (where typing capital `!@#$%` is intended), so
  // contenteditable focus is the wrong axis to filter on. Form tags
  // (note boxes, text inputs) keep their keys: digits there are text.
  useHotkeys(
    NUMBER_KEYS,
    (event, handler) => {
      const key = handler.keys?.join("+");
      const preset =
        WINDOW_PRESETS.find((p) => p.numberKey.toLowerCase() === `shift+${key}`) ??
        WINDOW_PRESETS.find((p) => p.numberKey === `Shift+${key}`);
      if (preset) {
        onPresetSelect(preset.id, preset.center, preset.width);
      }
    },
    {
      enabled: plainKeyEnabled,
      preventDefault: true,
      enableOnContentEditable: true,
    },
  );

  // Shift + letter mnemonics — hover-gated.
  useHotkeys(
    LETTER_KEYS,
    (event, handler) => {
      const key = handler.keys?.join("+");
      const preset = WINDOW_PRESETS.find((p) => p.letterKey.toLowerCase() === `shift+${key}`);
      if (preset) {
        onPresetSelect(preset.id, preset.center, preset.width);
      }
    },
    {
      enabled: plainKeyEnabled,
      preventDefault: true,
      enableOnContentEditable: true,
    },
  );

  // Alt+number — modifier binding, fires from anywhere (no hover gate).
  // alt+digits are free on Mac (option+digit) and Win/Linux Chrome
  // (cmd/ctrl+1..5 switch browser tabs and aren't page-overridable).
  useHotkeys(
    WINDOW_PRESETS.map((_, i) => `alt+${i + 1}`).join(","),
    (event, handler) => {
      const key = handler.keys?.join("+");
      const idx = key ? parseInt(key, 10) - 1 : -1;
      const preset = WINDOW_PRESETS[idx];
      if (preset) {
        onPresetSelect(preset.id, preset.center, preset.width);
      }
    },
    {
      enabled,
      preventDefault: true,
      enableOnContentEditable: true,
      enableOnFormTags: true,
    },
  );

  // Alt + letter mnemonic — modifier binding, fires from anywhere.
  useHotkeys(
    WINDOW_PRESETS.map((p) => `alt+${p.letterKey.split("+")[1].toLowerCase()}`).join(","),
    (event, handler) => {
      const key = handler.keys?.join("+");
      const preset = WINDOW_PRESETS.find((p) => p.letterKey.split("+")[1].toLowerCase() === key);
      if (preset) {
        onPresetSelect(preset.id, preset.center, preset.width);
      }
    },
    {
      enabled,
      preventDefault: true,
      enableOnContentEditable: true,
      enableOnFormTags: true,
    },
  );
}
